import React, { useState, useEffect, useRef } from "react";
import LocationFields from "@/components/LocationFields";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Sparkles, Car, Clock, Star, Coins, ArrowRightLeft, Award, Camera, ImagePlus, Loader2, Trash2 } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import ShareCard from "@/components/ShareCard";
import MediaAlbum from "@/components/MediaAlbum";
import SponsoredAdWidget from "@/components/SponsoredAdWidget";
import TopWeeklyPosts from "@/components/TopWeeklyPosts";
import CoinTransferDialog from "@/components/CoinTransferDialog";
import NotificationPreferences from "@/components/NotificationPreferences";
import WeeklyEarningsCard from "@/components/WeeklyEarningsCard";
import WhatsAppInviteCard from "@/components/WhatsAppInviteCard";
import ProfileMissionsCard from "@/components/ProfileMissionsCard";
import PrivacySettingsCard from "@/components/PrivacySettingsCard";
import SavedPostsCard from "@/components/SavedPostsCard";
import CoinsShareBanner from "@/components/CoinsShareBanner";
import { InvestorBanner, VideoAutoplayCard } from "@/components/ProfilePromoCards";
import { Link } from "react-router-dom";
import { Bell, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    picture: user?.picture || "",
    cover_url: user?.cover_url || "",
    city: user?.city || "",
    country: user?.country || "",
    state: user?.state || "",
    cep: user?.cep || "",
    street: user?.street || "",
    neighborhood: user?.neighborhood || "",
    address_number: user?.address_number || "",
    gender: user?.gender || "",
    interests: (user?.interests || []).join(", "),
    email: user?.email || "",
    phone: user?.phone || "",
    address: user?.address || "",
    pix_key: user?.pix_key || "",
  });
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [rides, setRides] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [coinsData, setCoinsData] = useState(null);
  const [convertAmount, setConvertAmount] = useState("");
  const [tiers, setTiers] = useState(null);
  const [referrals, setReferrals] = useState({ total: 0, recent: [] });

  useEffect(() => {
    axios.get(`${API}/rides/history`).then(r => setRides(r.data));
    if (user?.user_id) {
      axios.get(`${API}/users/${user.user_id}/ratings`).then(r => setRatings(r.data)).catch(() => {});
    }
    axios.get(`${API}/coins/me`).then(r => setCoinsData(r.data)).catch(() => {});
    axios.get(`${API}/tiers/me`).then(r => setTiers(r.data)).catch(() => {});
  }, [user?.user_id]);

  // Live poll of referrals count every 20s
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/users/me/referrals`);
        if (alive) setReferrals(r.data);
      } catch (err) { console.warn("[Profile] referrals load failed", err?.message); }
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [user?.user_id]);

  const claimTier = async (name) => {
    try {
      const r = await axios.post(`${API}/tiers/claim`, { tier_name: name });
      toast.success(`+${r.data.coin_bonus} FUTURO PDV creditados!`);
      const [tr, cr] = await Promise.all([axios.get(`${API}/tiers/me`), axios.get(`${API}/coins/me`)]);
      setTiers(tr.data); setCoinsData(cr.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const convert = async () => {
    const c = parseInt(convertAmount);
    if (!c || c <= 0) return toast.error("Informe uma quantidade válida");
    try {
      const res = await axios.post(`${API}/coins/convert`, { coins: c });
      toast.success(`+R$ ${res.data.brl_credited.toFixed(2)} adicionado ao saldo!`);
      setConvertAmount("");
      const cr = await axios.get(`${API}/coins/me`);
      setCoinsData(cr.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const save = async () => {
    try {
      const payload = {
        name: form.name,
        bio: form.bio,
        picture: form.picture,
        cover_url: form.cover_url,
        city: form.city,
        country: form.country,
        state: form.state,
        cep: form.cep,
        street: form.street,
        neighborhood: form.neighborhood,
        address_number: form.address_number,
        email: form.email?.trim() || undefined,
        phone: form.phone ?? "",
        address: form.address ?? "",
        pix_key: form.pix_key ?? "",
        gender: form.gender || null,
        interests: form.interests
          ? form.interests.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      const res = await axios.put(`${API}/auth/profile`, payload);
      setUser(res.data);
      toast.success("Perfil atualizado!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  // Direct-upload helper: sends file, saves the URL to the profile immediately,
  // and updates local form state so the UI reflects the change without a full save.
  const removePhoto = async (kind) => {
    const label = kind === "picture" ? "foto de perfil" : "foto de capa";
    if (!window.confirm(`Remover sua ${label} e voltar para o padrão?`)) return;
    try {
      const res = await axios.delete(`${API}/auth/profile/photo/${kind}`);
      setUser(res.data);
      setForm((f) => ({ ...f, picture: res.data.picture || "", cover_url: res.data.cover_url || "" }));
      toast.success(kind === "picture" ? "Foto de perfil removida — avatar padrão aplicado" : "Foto de capa removida");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao remover"); }
  };

  const uploadAndSave = async (file, field) => {
    if (!file) return;
    const setUploading = field === "picture" ? setUploadingAvatar : setUploadingCover;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Store relative /api/files/... path so avatar/cover survive domain moves.
      const url = up.data.url;
      const res = await axios.put(`${API}/auth/profile`, { [field]: url });
      setUser(res.data);
      setForm((f) => ({ ...f, [field]: url }));
      toast.success(field === "picture" ? "Foto de perfil atualizada!" : "Foto de capa atualizada!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no upload");
    } finally {
      setUploading(false);
    }
  };

  const goPremium = async () => {
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "premium",
        origin_url: window.location.origin,
        metadata: { type: "premium" },
      });
      window.location.href = res.data.checkout_url;
    } catch { toast.error("Erro"); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Card className="overflow-hidden" data-testid="profile-card">
        {/* Cover photo (click to upload) */}
        <button
          type="button"
          onClick={() => coverInputRef.current?.click()}
          data-testid="profile-cover-btn"
          aria-label="Trocar foto de capa"
          disabled={uploadingCover}
          className="relative w-full h-40 md:h-56 group block bg-gradient-to-br from-primary/25 via-primary/10 to-transparent overflow-hidden disabled:cursor-wait"
          style={form.cover_url ? { backgroundImage: `url(${form.cover_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
        >
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
            {uploadingCover ? (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/60 text-white text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Enviando capa…
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 text-black text-sm font-semibold shadow">
                <ImagePlus className="w-4 h-4" /> {form.cover_url ? "Trocar foto de capa" : "Adicionar foto de capa"}
              </div>
            )}
          </div>
          {!form.cover_url && !uploadingCover && (
            <div className="absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur text-white text-[11px] font-semibold">
              <ImagePlus className="w-3.5 h-3.5" /> Adicionar capa
            </div>
          )}
          {form.cover_url && !uploadingCover && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); removePhoto("cover"); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); removePhoto("cover"); } }}
              data-testid="profile-cover-remove"
              className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 hover:bg-red-600 backdrop-blur text-white text-[11px] font-semibold transition-colors cursor-pointer"
            >
              <Trash2 className="w-3.5 h-3.5" /> Remover capa
            </span>
          )}
        </button>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="profile-cover-input"
          onChange={(e) => { uploadAndSave(e.target.files?.[0], "cover_url"); e.target.value = ""; }}
        />

        {/* Header with clickable avatar */}
        <div className="px-6 pb-6 -mt-12 md:-mt-14">
          <div className="flex items-end gap-4">
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              data-testid="profile-avatar-btn"
              aria-label="Trocar foto de perfil"
              disabled={uploadingAvatar}
              className="relative group flex-shrink-0 disabled:cursor-wait"
            >
              <Avatar className="w-24 h-24 md:w-28 md:h-28 ring-4 ring-card">
                <AvatarImage src={form.picture} />
                <AvatarFallback className="text-2xl">{form.name[0]}</AvatarFallback>
              </Avatar>
              <span className="absolute bottom-1 right-1 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                {uploadingAvatar ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              </span>
              <span className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-[11px] font-bold uppercase tracking-widest transition-opacity">
                Trocar foto
              </span>
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="profile-avatar-input"
              onChange={(e) => { uploadAndSave(e.target.files?.[0], "picture"); e.target.value = ""; }}
            />
            {form.picture && !form.picture.includes("dicebear.com") && !uploadingAvatar && (
              <button
                type="button"
                onClick={() => removePhoto("picture")}
                data-testid="profile-avatar-remove"
                className="self-end mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-600 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remover foto
              </button>
            )}
            <div className="flex-1 pt-12 min-w-0">
              <h1 className="text-2xl font-bold tracking-tight truncate">{form.name}</h1>
              <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
              {user?.avg_rating && (
                <div className="flex items-center gap-1 mt-1" data-testid="profile-rating">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-semibold text-sm">{user.avg_rating.toFixed(1)}</span>
                  <span className="text-xs text-muted-foreground">({user.ratings_count} avaliações)</span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3 mt-6">
          <Input data-testid="profile-name" placeholder={t("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Textarea data-testid="profile-bio" placeholder={t("bio")} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} />
          <div className="flex gap-2">
            <Input data-testid="profile-picture" placeholder="URL da foto de perfil" value={form.picture} onChange={(e) => setForm({ ...form, picture: e.target.value })} />
            <ImageUploader
              testid="profile-upload-btn"
              variant="outline"
              size="default"
              label="Foto"
              onUploaded={(url) => setForm({ ...form, picture: url })}
            />
          </div>
          <div className="pt-2 space-y-2" data-testid="profile-personal-data">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Dados pessoais</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input data-testid="profile-email" type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="profile-phone" placeholder="Telefone / WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div className="md:col-span-2" data-testid="profile-location">
                <LocationFields form={form} setForm={setForm} idPrefix="profile" />
              </div>
              <Input data-testid="profile-street" placeholder="Rua / Logradouro" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="profile-address-number" placeholder="Número" value={form.address_number} onChange={(e) => setForm({ ...form, address_number: e.target.value })} />
                <Input data-testid="profile-neighborhood" placeholder="Bairro" value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} />
              </div>
              <Input data-testid="profile-address" placeholder="Complemento (apto, bloco, referência)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="md:col-span-2" />
              <Input data-testid="profile-pix-key" placeholder="Chave Pix (CPF, e-mail, telefone ou aleatória)" value={form.pix_key} onChange={(e) => setForm({ ...form, pix_key: e.target.value })} />
            </div>
            <p className="text-[11px] text-muted-foreground">A chave Pix salva aqui é preenchida automaticamente nos seus saques.</p>
          </div>
          <Button data-testid="profile-save" onClick={save} className="rounded-full">{t("save")}</Button>
        </div>
        <div className="mt-4 pt-4 border-t border-border space-y-3">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Personalização de anúncios</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <select
              data-testid="profile-gender"
              value={form.gender || ""}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
              className="h-10 rounded-md border border-border bg-background px-3 text-sm"
            >
              <option value="">Gênero (opcional)</option>
              <option value="male">Masculino</option>
              <option value="female">Feminino</option>
              <option value="other">Outro</option>
            </select>
          </div>
          <Input data-testid="profile-interests" placeholder="Interesses separados por vírgula (ex: tecnologia, moda, esportes)" value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })} />
          <p className="text-[11px] text-muted-foreground">Usamos essas informações apenas para mostrar anúncios relevantes.</p>
        </div>
        </div>
      </Card>

      {/* Sponsored "Projeto social" banner + Top 10 of the week (moved here from the home feed) */}
      <SponsoredAdWidget variant="card" limit={1} />
      <TopWeeklyPosts limit={10} />

      {/* Moved from the Feed: investor invite, share & earn, video autoplay preferences */}
      <InvestorBanner />
      <CoinsShareBanner />
      <VideoAutoplayCard />

      {/* Live referral counter */}
      <Card className="p-5 border-emerald-500/40 bg-emerald-500/5" data-testid="profile-referrals-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-bold tracking-tight">Pessoas que você indicou</h3>
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" title="Ao vivo" />
            </div>
            <div className="text-4xl font-bold tracking-tight text-emerald-700 dark:text-emerald-400" data-testid="profile-referrals-total">
              {referrals.total.toLocaleString("pt-BR")}
            </div>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              Compartilhe seu link único de convite e ganhe bônus a cada novo cadastro.
            </p>

            {/* Shareable invite link */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-3" data-testid="profile-invite-link-block">
              <Input
                readOnly
                data-testid="profile-invite-link-input"
                value={`${process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin}/?ref=${user?.user_id || ""}`}
                onFocus={(e) => e.target.select()}
                className="text-xs font-mono flex-1"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const link = `${process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin}/?ref=${user?.user_id || ""}`;
                  try {
                    await navigator.clipboard.writeText(link);
                    toast.success("Link copiado!");
                  } catch { toast.error("Não foi possível copiar"); }
                }}
                data-testid="profile-invite-copy-btn"
                className="gap-1 rounded-full"
              >
                Copiar link
              </Button>
              <Button
                size="sm"
                onClick={async () => {
                  const link = `${process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin}/?ref=${user?.user_id || ""}`;
                  const text = `Conheça o FUTURO PDV! Curta, trabalhe, dirija e acumule moedas: ${link}`;
                  if (navigator.share) {
                    try { await navigator.share({ title: "FUTURO PDV", text, url: link }); } catch { /* user cancelled */ }
                  } else {
                    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
                  }
                }}
                data-testid="profile-invite-share-btn"
                className="gap-1 rounded-full"
              >
                Compartilhar
              </Button>
            </div>

            {referrals.recent.length > 0 && (
              <div className="flex -space-x-2" data-testid="profile-referrals-avatars">
                {referrals.recent.slice(0, 8).map((r) => (
                  <Avatar key={r.user_id} className="w-8 h-8 border-2 border-background">
                    <AvatarImage src={r.picture} />
                    <AvatarFallback className="text-xs">{(r.name || "?")[0]}</AvatarFallback>
                  </Avatar>
                ))}
                {referrals.total > referrals.recent.length && (
                  <div className="w-8 h-8 rounded-full border-2 border-background bg-muted flex items-center justify-center text-[10px] font-semibold">
                    +{referrals.total - referrals.recent.length}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-gradient-to-br from-primary/10 via-transparent to-blue-500/10 border-primary/30">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-primary-foreground" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold tracking-tight">PDV Premium</h3>
            <p className="text-sm text-muted-foreground mb-3">Destaque em freelas, corridas com desconto, sem anúncios</p>
            <Button data-testid="premium-btn" onClick={goPremium} className="rounded-full">Assinar R$ 29,90/mês</Button>
          </div>
        </div>
      </Card>

      {/* Weekly earnings + Invite friends (two-column on desktop) */}
      <div className="grid gap-4 md:grid-cols-2">
        <WeeklyEarningsCard />
        <WhatsAppInviteCard />
      </div>

      <ProfileMissionsCard />

      {/* Coins card */}
      {coinsData && (
        <Card className="p-6 border-yellow-500/30 bg-gradient-to-br from-yellow-500/10 via-transparent to-orange-500/10">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg shadow-yellow-500/30">
              <Coins className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold tracking-tight">
                Seu saldo <span className="bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">FUTURO PDV</span>
              </h3>
              <p className="text-xs text-muted-foreground">Ganhe FUTURO PDV usando o app e converta em saldo real</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-yellow-600" data-testid="coins-balance">{coinsData.balance}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">FUTURO PDV</div>
              <div className="text-xs text-muted-foreground">= R$ {coinsData.brl_equivalent.toFixed(2)}</div>
            </div>
          </div>

          {coinsData.next_goal && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-semibold">{coinsData.next_goal.name}</span>
                <span className="text-muted-foreground">{coinsData.balance}/{coinsData.next_goal.target} FUTURO PDV</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-yellow-500 transition-all"
                  style={{ width: `${Math.min(100, (coinsData.balance / coinsData.next_goal.target) * 100)}%` }}
                />
              </div>
              <div className="text-xs text-muted-foreground mt-1">🎁 {coinsData.next_goal.reward}</div>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              data-testid="convert-coins-input"
              type="number"
              placeholder={`FUTURO PDV a converter (1 R$ = ${coinsData.coins_per_brl})`}
              value={convertAmount}
              onChange={(e) => setConvertAmount(e.target.value)}
              className="flex-1"
            />
            <Button data-testid="convert-coins-btn" onClick={convert} className="rounded-full">
              <ArrowRightLeft className="w-4 h-4 mr-2" /> Converter
            </Button>
          </div>
          <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-border">
            <div className="text-xs text-muted-foreground">
              Saldo carteira: <span className="font-bold text-green-600">R$ {(coinsData.wallet_balance || 0).toFixed(2)}</span>
            </div>
            <CoinTransferDialog onDone={() => axios.get(`${API}/coins/me`).then(r => setCoinsData(r.data))} />
          </div>
        </Card>
      )}

      {/* Share to earn more FUTURO PDV */}
      <ShareCard onCoinsAwarded={() => { axios.get(`${API}/coins/me`).then(r => setCoinsData(r.data)); }} />

      {/* Tiers card (Bronze / Prata / Ouro / Diamante) */}
      {tiers && (
        <Card className="p-6" data-testid="tiers-card">
          <div className="flex items-start gap-4 mb-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
              style={{ background: tiers.current_tier?.color || "linear-gradient(135deg,#94a3b8,#64748b)", boxShadow: `0 8px 24px ${(tiers.current_tier?.color || "#64748b")}40` }}
            >
              <Award className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold tracking-tight">
                Categoria atual: <span style={{ color: tiers.current_tier?.color || "#94a3b8" }} data-testid="tier-current-name">{tiers.current_tier?.name || "Sem categoria"}</span>
              </h3>
              <p className="text-xs text-muted-foreground">
                Atividade: <strong data-testid="tier-activity">{tiers.activity}</strong> ({tiers.jobs_completed} trabalhos + {tiers.rides_completed} viagens)
              </p>
              {tiers.next_tier && (
                <p className="text-xs text-muted-foreground mt-1">
                  Próximo: <strong>{tiers.next_tier.name}</strong> em {Math.max(0, tiers.next_tier.activity_target - tiers.activity)} atividade(s) → <span className="text-yellow-600 font-bold">+{tiers.next_tier.coin_bonus} FUTURO PDV</span>
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {tiers.all_tiers.map(t => (
              <div
                key={t.name}
                className={`p-3 rounded-lg border-2 text-center transition ${t.reached ? "opacity-100" : "opacity-40"}`}
                style={{ borderColor: t.color + (t.reached ? "" : "44") }}
                data-testid={`tier-item-${t.name}`}
              >
                <Award className="w-6 h-6 mx-auto mb-1" style={{ color: t.color }} />
                <div className="text-sm font-bold" style={{ color: t.color }}>{t.name}</div>
                <div className="text-[10px] text-muted-foreground mb-1">{t.activity_target} atividades</div>
                <div className="text-[10px] font-bold text-yellow-600">+{t.coin_bonus} FUTURO PDV</div>
                {t.perks && <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{t.perks}</div>}
                {t.reached && !t.claimed && (
                  <Button size="sm" className="w-full mt-2 h-7 rounded-full text-[11px]" data-testid={`tier-claim-${t.name}`} onClick={() => claimTier(t.name)}>Resgatar</Button>
                )}
                {t.claimed && <div className="text-[10px] text-green-600 font-semibold mt-1">✓ Resgatado</div>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Media album */}
      <MediaAlbum userId={user?.user_id} />

      {/* Ratings received */}
      {ratings.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4 tracking-tight flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" /> Avaliações recebidas
          </h2>
          <div className="space-y-3">
            {ratings.slice(0, 5).map(r => (
              <div key={r.rating_id} className="p-3 rounded-lg border border-border" data-testid={`rating-${r.rating_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium">{r.from_name}</div>
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= r.stars ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground/30"}`} />
                    ))}
                  </div>
                </div>
                {r.comment && <p className="text-xs text-muted-foreground">{r.comment}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4 tracking-tight flex items-center gap-2">
          <Car className="w-5 h-5" /> Histórico de corridas
        </h2>
        {rides.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma corrida ainda</p>}
        <div className="space-y-3">
          {rides.map(r => (
            <div key={r.ride_id} className="flex items-center gap-3 p-3 rounded-lg border border-border">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                {r.vehicle_type === "moto" ? "🏍" : "🚗"}
              </div>
              <div className="flex-1">
                <div className="font-medium text-sm">{r.dest_address?.substring(0, 40)}...</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <Clock className="w-3 h-3" /> {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold">R$ {r.price?.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground">{r.distance_km} km</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Privacy & safety */}
      <div id="privacy"><PrivacySettingsCard /></div>
      <SavedPostsCard />

      {/* Notifications preferences (compact card + link to full page) */}
      <Card className="p-6" data-testid="profile-notifications-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Bell className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">Notificações</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Escolha em quais canais você quer receber cada tipo de aviso (app, e-mail e SMS).
              </p>
            </div>
          </div>
          <Link
            to="/settings/notifications"
            className="text-xs text-primary underline underline-offset-4 flex items-center gap-1 shrink-0"
            data-testid="profile-notif-open-full"
          >
            Abrir tela cheia <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        <NotificationPreferences compact />
      </Card>
    </div>
  );
}
